/**
 * RecurrenceSheet — responsive sheet wrapper for RecurrencePicker.
 *
 * Matches the pattern of DatePickerSheet and PlanSheet.
 * Handles create, edit, and delete flows for task recurrence.
 */

import { memo, useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import dayjs from "dayjs"
import { ResponsiveSheet } from "@/app/components/ui/responsive-sheet"
import { RecurrencePicker, configToRecurrenceOpts, type RecurrenceConfig } from "./RecurrencePicker"
import { parseRruleToConfig } from "@totodo/operations"
import { RecurrenceBadge } from "./RecurrenceBadge"
import { useRecurrence, useOpenInstanceCount } from "./useRecurrence"
import { createRecurrence, updateRecurrence, deleteRecurrence, generateFixedInstances, pauseRecurrence, resumeRecurrence, removeExcludeDate, getTaskReminder, deleteFutureInstances } from "@/db/operations"
import { db, type DbObject, type DbStartAtIndex } from "@/db/schema"
import { useLiveQueryImmediate } from "@/db/hooks"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/app/components/ui/dialog"
import { Button } from "@/app/components/ui/button"
import { parseDbDate } from "@totodo/operations"

interface RecurrenceSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  task: DbObject
  startAtData?: DbStartAtIndex
}

export const RecurrenceSheet = memo(function RecurrenceSheet({
  open,
  onOpenChange,
  task,
  startAtData: startAtDataProp,
}: RecurrenceSheetProps) {
  const { t } = useTranslation()
  const taskId = task.id!
  const recurrence = useRecurrence(taskId)
  const openCount = useOpenInstanceCount(taskId)
  const [confirmDelete, setConfirmDelete] = useState(false)

  // Fetch startAtData if not provided as prop
  const [fetchedStartAtData] = useLiveQueryImmediate(
    () => startAtDataProp ? undefined : db.startAtIndex.get(`#${taskId}`),
    [taskId, startAtDataProp],
    undefined,
  )
  const startAtData = startAtDataProp ?? fetchedStartAtData

  // Derive initial config from existing recurrence
  const initialConfig = useMemo((): Partial<RecurrenceConfig> | undefined => {
    if (!recurrence) return undefined

    // Parse RRULE → frequency/interval/weekdays using central parser
    const parsed = recurrence.rrule ? parseRruleToConfig(recurrence.rrule) : undefined

    return {
      mode: recurrence.mode,
      frequency: parsed?.frequency,
      interval: parsed?.interval,
      weekdays: parsed?.weekdays,
      monthDay: parsed?.monthDay,
      monthWeek: parsed?.monthWeek,
      monthWeekday: parsed?.monthWeekday,
      pilePolicy: recurrence.pilePolicy,
      pileCap: recurrence.pileCap,
      afterCompletionDays: recurrence.afterCompletionDays ?? 1,
      afterCompletionTime: recurrence.afterCompletionTime,
      weekdayTimes: recurrence.weekdayTimes ? JSON.parse(recurrence.weekdayTimes) : undefined,
      reminderOffsets: recurrence.reminderOffsets
        ? JSON.parse(recurrence.reminderOffsets)
        : recurrence.reminderOffsetMs != null
          ? [{ offsetMs: recurrence.reminderOffsetMs, kind: recurrence.reminderKind ?? "reminder" }]
          : [],
      deadlineOffset: recurrence.deadlineOffset,
      showInTodayDaysBefore: recurrence.showInTodayDaysBefore,
      endType: recurrence.maxCount ? "count" : recurrence.endDate ? "date" : "never",
      endCount: recurrence.maxCount,
      endDate: recurrence.endDate,
      flexibleCount: recurrence.flexibleCount,
      flexiblePeriod: recurrence.flexiblePeriod,
      flexibleResetDay: recurrence.flexibleResetDay,
      targetContainerId: recurrence.targetContainerId,
      progressiveField: recurrence.progressiveField,
      progressiveStart: recurrence.progressiveStart,
      progressiveIncrement: recurrence.progressiveIncrement,
      progressiveUnit: recurrence.progressiveUnit,
      progressiveMax: recurrence.progressiveMax,
      progressivePer: recurrence.progressivePer,
      autoFocus: recurrence.autoFocus === 1,
      aiEnabled: recurrence.aiEnabled === 1,
      aiPrompt: recurrence.aiPrompt,
    }
  }, [recurrence])

  const handleSave = useCallback(async (config: RecurrenceConfig) => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const startAtMs = parseDbDate(startAtData?.startAt) ?? dayjs().startOf("day").valueOf()

    const opts = configToRecurrenceOpts(taskId, config, timezone, startAtMs)

    // Auto-inherit reminder offset from template's existing reminder (only if none configured)
    if (opts.reminderOffsetMs == null && (!opts.reminderOffsets || opts.reminderOffsets.length === 0)) {
      const templateReminder = await getTaskReminder(taskId)
      const templateFireMs = parseDbDate(templateReminder?.fireAt)
      if (templateFireMs !== undefined) {
        opts.reminderOffsetMs = templateFireMs - startAtMs
        opts.reminderKind = "reminder"
        opts.reminderOffsets = [{ offsetMs: opts.reminderOffsetMs, kind: "reminder" }]
      }
    }

    if (recurrence) {
      // Update existing — serialize reminderOffsets for DB storage
      const dbUpdates = {
        ...opts,
        reminderOffsets: opts.reminderOffsets ? JSON.stringify(opts.reminderOffsets) : undefined,
      }
      await updateRecurrence(taskId, dbUpdates)
      // Delete + regenerate if FIXED (ensures correct times/reminders)
      if (opts.mode === "FIXED") {
        await deleteFutureInstances(taskId)
        await generateFixedInstances(taskId, 30)
      }
    } else {
      // Create new + generate initial instances
      const result = await createRecurrence(opts)
      if (result.ok && opts.mode === "FIXED") {
        await generateFixedInstances(taskId, 30)
      }
    }
    onOpenChange(false)
  }, [taskId, recurrence, startAtData, onOpenChange])

  const handleCancel = useCallback(() => {
    onOpenChange(false)
  }, [onOpenChange])

  const handleDelete = useCallback(async (deleteFuture: boolean) => {
    await deleteRecurrence(taskId, { deleteFutureInstances: deleteFuture })
    setConfirmDelete(false)
    onOpenChange(false)
  }, [taskId, onOpenChange])

  const handlePauseToggle = useCallback(async () => {
    if (recurrence?.pausedAt) {
      await resumeRecurrence(taskId)
    } else {
      await pauseRecurrence(taskId)
    }
  }, [taskId, recurrence])

  return (
    <>
    <ResponsiveSheet
      open={open}
      onOpenChange={(v) => { if (!v) setConfirmDelete(false); onOpenChange(v) }}
      title={t("recurrence.title", "Repeat")}
    >
      <div className="flex flex-col gap-4 overflow-y-auto px-4 pt-2 pb-4">
            {/* Show existing recurrence summary */}
            {recurrence && (
              <div className="flex items-center rounded-full bg-muted/50 px-4 py-2.5">
                <RecurrenceBadge recurrence={recurrence} />
              </div>
            )}

            {/* Exclude dates */}
            {recurrence && recurrence.excludeDates && recurrence.excludeDates.length > 0 && (
              <div className="rounded-2xl bg-muted/30 p-3">
                <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                  {t("recurrence.excludedDates", "Excluded dates")}
                </p>
                <div className="flex flex-wrap gap-1">
                  {recurrence.excludeDates.map((d) => (
                    <span
                      key={d}
                      className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs"
                    >
                      {d}
                      <button
                        onClick={() => void removeExcludeDate(taskId, d)}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Completion analytics — moved to /recurring page for cleaner sheet */}

            <RecurrencePicker
              templateId={taskId}
              templateParentPath={task.parentPath}
              initialConfig={initialConfig}
              taskStartAt={parseDbDate(startAtData?.startAt)}
              onSave={handleSave}
              onCancel={handleCancel}
              onPause={recurrence ? handlePauseToggle : undefined}
              isPaused={!!recurrence?.pausedAt}
              onDelete={recurrence ? () => setConfirmDelete(true) : undefined}
            />
      </div>
    </ResponsiveSheet>

    {/* Delete confirmation dialog — proper modal like ProjectDeleteDialog */}
    <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>
            {t("recurrence.deleteConfirmTitle", "Remove repeating schedule?")}
          </DialogTitle>
          {openCount > 0 && (
            <DialogDescription>
              {t("recurrence.deleteConfirmInstances", {
                count: openCount,
                defaultValue: "There are {{count}} open instance(s). Delete them too?",
              })}
            </DialogDescription>
          )}
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" className="text-muted-foreground" onClick={() => setConfirmDelete(false)}>
            {t("Cancel")}
          </Button>
          {openCount > 0 ? (
            <>
              <Button variant="ghost" className="text-muted-foreground" onClick={() => handleDelete(false)}>
                {t("recurrence.keepInstances", "Keep instances")}
              </Button>
              <Button variant="error" onClick={() => handleDelete(true)}>
                {t("recurrence.deleteAll", "Delete all")}
              </Button>
            </>
          ) : (
            <Button variant="error" onClick={() => handleDelete(false)}>
              {t("Remove")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  )
})

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
